import * as React from 'react'
import styled from 'styled-components'
import { tJa } from '../../../core/i18n'
import * as constants from '../../../static/constants'
import { HOUR_OPTIONS, MINUTE_OPTIONS } from '../../../static/constants_ota_pending'
import { IDmcTourGuestParams } from '../../../core/services/DmcTourService'
import { InputText, InputTextArea, Select } from '../../atoms'
import Required from '../../atoms/required'
import SvgIcon from '../../atoms/SvgIcon'

// 未登録ゲスト(画面上の識別用に key を持つ)
export type IGuestFormValues = IDmcTourGuestParams & { key: string }

// 登録済みゲスト(表示専用。編集はOTA予約画面に委ねる)
export interface IRegisteredGuest {
  id: number
  jwt_tour_id: number | null
  channel_manager: string
  seller: string
  tour_date: string
  tour_start_at: string
  customer_full_name: string
  customer_last_name: string
  customer_first_name: string
  customer_email: string
  customer_phone: string
  other_requests: string
  num_of_adults: number
  num_of_children: number
  num_of_infants: number
  duration: number | null
}

let guestKeySeq = 0
const nextGuestKey = () => `guest-${Date.now()}-${guestKeySeq++}`

export const buildEmptyGuest = (): IGuestFormValues => ({
  key: nextGuestKey(),
  channel_manager: '',
  seller: '',
  tour_date: '',
  tour_start_at: '09:00',
  customer_last_name: '',
  customer_first_name: '',
  customer_email: '',
  customer_phone: '',
  num_of_adults: '0',
  num_of_children: '0',
  num_of_infants: '0',
  other_requests: '',
  duration: '',
})

// 所要時間は通常ツアーの編集画面と同じ選択肢
const DURATION_OPTIONS = [{ value: '', label: tJa('generic.select') }].concat(
  constants.POST_DURATIONS.map(hours => ({ value: String(hours), label: String(hours) }))
)

const PEOPLE_FIELDS = ['num_of_adults', 'num_of_children', 'num_of_infants'] as const

const countPeople = (guest: { num_of_adults: any; num_of_children: any; num_of_infants: any }) =>
  (Number(guest.num_of_adults) || 0) +
  (Number(guest.num_of_children) || 0) +
  (Number(guest.num_of_infants) || 0)

interface IProps {
  guests: IGuestFormValues[]
  onChange: (guests: IGuestFormValues[]) => void
  registeredGuests: IRegisteredGuest[]
}

// ラベルを上に置く入力欄(モックのゲストカード内レイアウト)
const Field: React.FC<{ label: string; required?: boolean }> = props => (
  <FieldWrapper>
    <label>
      {props.label}
      {props.required && <Required />}
    </label>
    {props.children}
  </FieldWrapper>
)

const guestSummary = (name: string, people: number) =>
  [name.trim(), `${people}${tJa('post.people')}`].filter(Boolean).join(' ・ ')

export const GuestForm: React.FC<IProps> = ({ guests, onChange, registeredGuests }) => {
  // 折りたたみ中のゲスト(key)。追加直後のゲストは展開して表示する
  const [collapsedKeys, setCollapsedKeys] = React.useState<string[]>([])
  const toggleCollapsed = (key: string) =>
    setCollapsedKeys(keys => (keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key]))

  // 追加/複製したゲストのカードまでスクロールする
  const cardRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({})
  const [scrollToKey, setScrollToKey] = React.useState<string | null>(null)
  React.useEffect(() => {
    if (!scrollToKey) {
      return
    }
    cardRefs.current[scrollToKey]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setScrollToKey(null)
  }, [scrollToKey])

  const updateGuest = (key: string, patch: Partial<IGuestFormValues>) =>
    onChange(guests.map(guest => (guest.key === key ? { ...guest, ...patch } : guest)))
  const removeGuest = (key: string) => onChange(guests.filter(guest => guest.key !== key))
  const appendGuest = (guest: IGuestFormValues) => {
    onChange([...guests, guest])
    setScrollToKey(guest.key)
  }
  const addGuest = () => appendGuest(buildEmptyGuest())
  // 複製: 同じ内容のゲストを未登録ゲストとして末尾に追加する
  const duplicateGuest = (source: Omit<IGuestFormValues, 'key'>) =>
    appendGuest({ ...source, key: nextGuestKey() })
  // 登録済みゲストの複製: 未登録ゲストの複製と同じく、ゲスト名・連絡先・要望も含めて全項目をコピーする
  const duplicateRegisteredGuest = (guest: IRegisteredGuest) =>
    duplicateGuest({
      ...buildEmptyGuest(),
      channel_manager: guest.channel_manager,
      seller: guest.seller,
      tour_date: guest.tour_date,
      tour_start_at: guest.tour_start_at,
      customer_last_name: guest.customer_last_name,
      customer_first_name: guest.customer_first_name,
      customer_email: guest.customer_email,
      customer_phone: guest.customer_phone,
      other_requests: guest.other_requests,
      num_of_adults: String(guest.num_of_adults),
      num_of_children: String(guest.num_of_children),
      num_of_infants: String(guest.num_of_infants),
      duration: guest.duration == null ? '' : String(guest.duration),
    })

  const totalGroups = registeredGuests.length + guests.length
  const totalPeople =
    registeredGuests.reduce((sum, guest) => sum + countPeople(guest), 0) +
    guests.reduce((sum, guest) => sum + countPeople(guest), 0)

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{tJa('generic.guests')}</SectionTitle>
        <span className="summary">
          {tJa('dmc_tour.guests_summary', { groups: totalGroups, people: totalPeople })}
        </span>
      </SectionHeader>

      {registeredGuests.map((guest, index) => (
        <GuestCard key={`registered-${guest.id}`} className="registered">
          <GuestCardHeader>
            <div className="title">
              <SvgIcon name="groups" width={20} height={20} />
              {tJa('dmc_tour.guest_no', { no: index + 1 })}
              <span className="badge">{tJa('dmc_tour.registered_guest')}</span>
              <span className="sub">
                {guestSummary(guest.customer_full_name, countPeople(guest))}
              </span>
            </div>
            <div className="actions">
              <button type="button" onClick={() => duplicateRegisteredGuest(guest)}>
                <SvgIcon name="add_circle" width={16} height={16} />
                {tJa('dmc_tour.duplicate_guest')}
              </button>
              <a
                href={`/agent/ota_reservations/${guest.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SvgIcon name="link" width={16} height={16} />
                {tJa('dmc_tour.open_ota_reservation')}
              </a>
            </div>
          </GuestCardHeader>
          <ReadOnlyRow>
            <span>
              {tJa('dmc_tour.agent_name')}: {guest.channel_manager}
            </span>
            <span>
              {tJa('dmc_tour.emergency_contact')}: {guest.seller || '-'}
            </span>
            <span>
              {tJa('dmc_tour.tour_date')}: {guest.tour_date} {guest.tour_start_at}
            </span>
            <span>
              {tJa('dmc_tour.duration')}: {guest.duration ?? '-'}
            </span>
          </ReadOnlyRow>
          <Note>{tJa('dmc_tour.registered_guest_note')}</Note>
        </GuestCard>
      ))}

      {guests.map((guest, index) => {
        const collapsed = collapsedKeys.includes(guest.key)
        const [hour, minute] = (guest.tour_start_at || '09:00').split(':')
        // 複製元(登録済みゲスト)の時刻が選択肢に無い場合は、その値を選択肢に足して表示する
        const hourOptions = HOUR_OPTIONS.some(option => option.value === hour)
          ? HOUR_OPTIONS
          : [...HOUR_OPTIONS, { value: hour, label: hour }]
        const minuteOptions = MINUTE_OPTIONS.some(option => option.value === minute)
          ? MINUTE_OPTIONS
          : [...MINUTE_OPTIONS, { value: minute, label: minute }]
        return (
          <GuestCard key={guest.key} ref={element => (cardRefs.current[guest.key] = element)}>
            <GuestCardHeader>
              <div className="title">
                <SvgIcon name="groups" width={20} height={20} />
                {tJa('dmc_tour.guest_no', { no: registeredGuests.length + index + 1 })}
                {collapsed && (
                  <span className="sub">
                    {guestSummary(
                      `${guest.customer_last_name} ${guest.customer_first_name}`,
                      countPeople(guest)
                    )}
                  </span>
                )}
              </div>
              <div className="actions">
                <button type="button" onClick={() => duplicateGuest(guest)}>
                  <SvgIcon name="add_circle" width={16} height={16} />
                  {tJa('dmc_tour.duplicate_guest')}
                </button>
                <button type="button" className="danger" onClick={() => removeGuest(guest.key)}>
                  <SvgIcon name="delete_forever" width={16} height={16} />
                  {tJa('generic.delete')}
                </button>
                <button
                  type="button"
                  className="toggle"
                  aria-label={tJa(collapsed ? 'dmc_tour.expand_guest' : 'dmc_tour.collapse_guest')}
                  onClick={() => toggleCollapsed(guest.key)}
                >
                  <SvgIcon
                    name={collapsed ? 'expand_more' : 'expand_less'}
                    width={20}
                    height={20}
                  />
                </button>
              </div>
            </GuestCardHeader>
            {!collapsed && (
              <>
                <BlockTitle>{tJa('dmc_tour.agent_info')}</BlockTitle>
                <FieldGrid columns={2} className="agent-info">
                  <Field label={tJa('dmc_tour.agent_name')} required={true}>
                    <InputText
                      name={`channel_manager_${guest.key}`}
                      value={guest.channel_manager}
                      onChangeHandler={e =>
                        updateGuest(guest.key, { channel_manager: e.target.value })
                      }
                    />
                  </Field>
                  <Field label={tJa('dmc_tour.emergency_contact')}>
                    <InputText
                      name={`seller_${guest.key}`}
                      placeholder={tJa('dmc_tour.emergency_contact_placeholder')}
                      value={guest.seller}
                      onChangeHandler={e => updateGuest(guest.key, { seller: e.target.value })}
                    />
                  </Field>
                </FieldGrid>

                <BlockTitle>{tJa('dmc_tour.tour_info')}</BlockTitle>
                <FieldGrid columns={3} className="tour-info">
                  <Field label={tJa('dmc_tour.tour_date')} required={true}>
                    <InputText
                      type="date"
                      name={`tour_date_${guest.key}`}
                      value={guest.tour_date}
                      onChangeHandler={e => updateGuest(guest.key, { tour_date: e.target.value })}
                    />
                  </Field>
                  <Field label={tJa('dmc_tour.tour_start_at')} required={true}>
                    <TimeSelectRow>
                      <Select
                        name={`tour_start_hour_${guest.key}`}
                        options={hourOptions}
                        value={hour}
                        onChangeHandler={e =>
                          updateGuest(guest.key, { tour_start_at: `${e.target.value}:${minute}` })
                        }
                      />
                      <span>:</span>
                      <Select
                        name={`tour_start_minute_${guest.key}`}
                        options={minuteOptions}
                        value={minute}
                        onChangeHandler={e =>
                          updateGuest(guest.key, { tour_start_at: `${hour}:${e.target.value}` })
                        }
                      />
                    </TimeSelectRow>
                  </Field>
                  <Field label={tJa('dmc_tour.duration')} required={true}>
                    <Select
                      name={`duration_${guest.key}`}
                      options={DURATION_OPTIONS}
                      value={guest.duration}
                      onChangeHandler={e => updateGuest(guest.key, { duration: e.target.value })}
                    />
                  </Field>
                </FieldGrid>

                <BlockTitle>{tJa('generic.guests')}</BlockTitle>
                <FieldGrid columns={4} className="guest-info">
                  <Field label={tJa('dmc_tour.last_name')} required={true}>
                    <InputText
                      name={`customer_last_name_${guest.key}`}
                      value={guest.customer_last_name}
                      onChangeHandler={e =>
                        updateGuest(guest.key, { customer_last_name: e.target.value })
                      }
                    />
                  </Field>
                  <Field label={tJa('dmc_tour.first_name')} required={true}>
                    <InputText
                      name={`customer_first_name_${guest.key}`}
                      value={guest.customer_first_name}
                      onChangeHandler={e =>
                        updateGuest(guest.key, { customer_first_name: e.target.value })
                      }
                    />
                  </Field>
                  <Field label={tJa('generic.email')} required={true}>
                    <InputText
                      name={`customer_email_${guest.key}`}
                      inputMode="email"
                      value={guest.customer_email}
                      onChangeHandler={e =>
                        updateGuest(guest.key, { customer_email: e.target.value })
                      }
                    />
                  </Field>
                  <Field label={tJa('generic.tel')}>
                    <InputText
                      type="tel"
                      name={`customer_phone_${guest.key}`}
                      value={guest.customer_phone}
                      onChangeHandler={e =>
                        updateGuest(guest.key, { customer_phone: e.target.value })
                      }
                    />
                  </Field>
                </FieldGrid>
                <BlockTitle className="sub">{tJa('dmc_tour.num_of_people')}</BlockTitle>
                <FieldGrid columns={4} className="num_of_people-info">
                  {PEOPLE_FIELDS.map(field => (
                    <Field key={field} label={tJa(`dmc_tour.${field}`)}>
                      <InputText
                        type="number"
                        inputMode="numeric"
                        name={`${field}_${guest.key}`}
                        value={guest[field]}
                        onChangeHandler={e => updateGuest(guest.key, { [field]: e.target.value })}
                      />
                    </Field>
                  ))}
                  <Field label={tJa('dmc_tour.subtotal')}>
                    <InputText
                      name={`subtotal_${guest.key}`}
                      value={countPeople(guest)}
                      readonly={true}
                      disabled={true}
                    />
                  </Field>
                </FieldGrid>
                <Field label={tJa('dmc_tour.other_requests')}>
                  <InputTextArea
                    name={`other_requests_${guest.key}`}
                    value={guest.other_requests}
                    onChangeHandler={e =>
                      updateGuest(guest.key, { other_requests: e.target.value })
                    }
                  />
                </Field>
              </>
            )}
          </GuestCard>
        )
      })}

      <AddGuestButton type="button" onClick={addGuest}>
        <SvgIcon name="add" width={18} height={18} />
        {tJa('dmc_tour.add_guest')}
      </AddGuestButton>
    </Section>
  )
}

const Section = styled.section`
  margin-bottom: 24px;
  padding: 20px 24px;
  background-color: ${constants.COLORS.white};
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 6px;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: -20px -24px 20px;
  padding: 14px 24px;
  border-bottom: 1px solid ${constants.COLORS.divider2};

  .summary {
    font-size: 12px;
    color: ${constants.COLORS.subText};
  }
`

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: bold;
`

const GuestCard = styled.div`
  padding: 0 16px 16px;
  overflow: hidden;
  // スクロール時にサイトヘッダーの下に隠れないようにする
  scroll-margin-top: ${constants.SCROLLING_HEADER_HEIGHT + 16}px;
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 8px;

  & + & {
    margin-top: 12px;
  }
`

// カード幅いっぱいのグレー帯にする(カードの左右paddingを打ち消す)
const GuestCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 0 -16px;
  padding: 12px 16px;
  background-color: ${constants.COLORS.background};
  border-bottom: 1px solid ${constants.COLORS.divider2};

  .title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-weight: bold;

    svg {
      flex-shrink: 0;
      color: ${constants.COLORS.subText};
    }
  }

  .badge {
    padding: 2px 8px;
    font-size: 11px;
    font-weight: normal;
    color: ${constants.COLORS.white};
    background-color: ${constants.COLORS.subText};
    border-radius: 10px;
  }

  .sub {
    overflow: hidden;
    font-size: 13px;
    font-weight: normal;
    color: ${constants.COLORS.subText};
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;

    button,
    a {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 6px 8px;
      font-size: 13px;
      color: ${constants.COLORS.subText};
      background: none;
      border: none;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        color: ${constants.COLORS.primary};
        background-color: ${constants.COLORS.lightGray};
      }

      &.danger {
        color: ${constants.COLORS.danger};
      }
    }

    .toggle {
      padding: 4px;
    }
  }
`

const BlockTitle = styled.div`
  margin: 16px 0 8px;
  font-size: 14px;
  font-weight: bold;

  &.sub {
    margin-top: 4px;
    font-size: 13px;
  }
`

const FieldGrid = styled.div<{ columns: number }>`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${constants.COLORS.lightGray};
  &.agent-info input {
    min-width: 320px;
  }
  &.guest-info {
    border-bottom: 0;
    padding-bottom: 0;
    input {
      min-width: 200px;
    }
  }
  &.num_of_people-info {
    border-bottom: 0;
    input {
      width: 80px;
    }
  }
`

const FieldWrapper = styled.div`
  margin-bottom: 12px;

  > label {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: bold;
    color: ${constants.COLORS.subText};
  }
`

const ReadOnlyRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin-top: 12px;
  font-size: 13px;
`

const Note = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${constants.COLORS.subText};
`

const TimeSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  > div {
    flex: 1;
  }
`

const AddGuestButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 16px;
  padding: 12px;
  font-size: 14px;
  font-weight: bold;
  color: ${constants.COLORS.primary};
  background-color: ${constants.COLORS.background};
  border: 1px dashed ${constants.COLORS.divider2};
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.25s;

  &:hover {
    background-color: ${constants.COLORS.successBG};
  }
`
